import React, { useState, useEffect } from 'react';
import './AdminMenu.css';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { DEFAULT_MENU } from '../OurMenu';

const DEFAULT_MILKS = [
    { name: 'Whole', available: true },
    { name: 'Oat', available: true },
];

const blankItem = () => ({ name: '', desc: '', price: '', hidden: false });
const blankSection = () => ({ category: 'New Section', note: '', items: [blankItem()] });

// deep clone so edits never mutate the imported default
const clone = (data) => JSON.parse(JSON.stringify(data));

function AdminMenu() {
    const { currentUser, isAdmin, loading: authLoading, login, logout } = useAuth();
    const [sections, setSections] = useState([]);
    const [milkOptions, setMilkOptions] = useState(clone(DEFAULT_MILKS));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [status, setStatus] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);

    const isAuthorized = currentUser && isAdmin && currentUser.email === 'info@offftheroast.com';

    useEffect(() => {
        if (!isAuthorized) {
            setLoading(false);
            return;
        }
        (async () => {
            try {
                const snap = await getDoc(doc(db, 'siteContent', 'menu'));
                const data = snap.data();
                if (data && Array.isArray(data.sections) && data.sections.length > 0) {
                    setSections(clone(data.sections));
                } else {
                    setSections(clone(DEFAULT_MENU));
                }
                if (data && Array.isArray(data.milkOptions) && data.milkOptions.length > 0) {
                    setMilkOptions(clone(data.milkOptions));
                } else {
                    setMilkOptions(clone(DEFAULT_MILKS));
                }
            } catch {
                setSections(clone(DEFAULT_MENU));
            }
            setLoading(false);
        })();
    }, [isAuthorized]);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoginLoading(true);
        try {
            await login(email, password);
        } catch {
            setLoginError('Invalid email or password.');
        }
        setLoginLoading(false);
    };

    // ---- editing helpers ----
    const update = (next) => {
        setSections(next);
        setStatus('');
    };
    const setSectionName = (si, value) => {
        const next = clone(sections);
        next[si].category = value;
        update(next);
    };
    const setSectionNote = (si, value) => {
        const next = clone(sections);
        next[si].note = value;
        update(next);
    };
    const setItemField = (si, ii, field, value) => {
        const next = clone(sections);
        next[si].items[ii][field] = value;
        update(next);
    };
    const addItem = (si) => {
        const next = clone(sections);
        next[si].items.push(blankItem());
        update(next);
    };
    const removeItem = (si, ii) => {
        const next = clone(sections);
        next[si].items.splice(ii, 1);
        update(next);
    };
    const moveItem = (si, ii, dir) => {
        const next = clone(sections);
        const items = next[si].items;
        const j = ii + dir;
        if (j < 0 || j >= items.length) return;
        [items[ii], items[j]] = [items[j], items[ii]];
        update(next);
    };
    const addSection = () => {
        update([...clone(sections), blankSection()]);
    };
    const removeSection = (si) => {
        const next = clone(sections);
        next.splice(si, 1);
        update(next);
    };
    const moveSection = (si, dir) => {
        const next = clone(sections);
        const j = si + dir;
        if (j < 0 || j >= next.length) return;
        [next[si], next[j]] = [next[j], next[si]];
        update(next);
    };

    const handleSave = async () => {
        // strip fully-empty rows so the public page stays clean
        const cleaned = sections
            .map((s) => ({
                category: (s.category || '').trim(),
                note: (s.note || '').trim(),
                items: (s.items || [])
                    .filter((it) => (it.name || '').trim() !== '')
                    .map((it) => ({
                        name: (it.name || '').trim(),
                        desc: (it.desc || '').trim(),
                        price: (it.price || '').trim(),
                        hidden: it.hidden === true,
                    })),
            }))
            .filter((s) => s.category !== '' && s.items.length > 0);

        if (cleaned.length === 0) {
            setStatus('Add at least one section with one named item before saving.');
            return;
        }

        setSaving(true);
        setStatus('');
        try {
            await setDoc(doc(db, 'siteContent', 'menu'), {
                sections: cleaned,
                milkOptions: milkOptions.map((m) => ({
                    name: m.name,
                    available: m.available !== false,
                })),
                updatedAt: serverTimestamp(),
                updatedBy: currentUser.email,
            });
            setStatus('✓ Saved. Your live menu is updated.');
        } catch (err) {
            setStatus('Could not save: ' + (err.message || 'unknown error'));
        }
        setSaving(false);
    };

    const toggleMilk = (idx) => {
        const next = clone(milkOptions);
        next[idx].available = !next[idx].available;
        setMilkOptions(next);
        setStatus('');
    };

    const resetToDefault = () => {
        if (window.confirm('Replace the editor with the original default menu? Nothing is saved until you press Save Menu.')) {
            update(clone(DEFAULT_MENU));
        }
    };

    // Wait for auth to resolve before showing the login form
    if (authLoading) return null;

    if (!currentUser) {
        return (
            <div className='am-container'>
                <h1 className='am-title'>ADMIN</h1>
                <form className='am-login' onSubmit={handleLogin}>
                    <h2>Sign In</h2>
                    {loginError && <div className='am-login__error'>{loginError}</div>}
                    <div className='am-login__group'>
                        <label>Email</label>
                        <input type='email' value={email} onChange={(e) => setEmail(e.target.value)} required />
                    </div>
                    <div className='am-login__group'>
                        <label>Password</label>
                        <input type='password' value={password} onChange={(e) => setPassword(e.target.value)} required />
                    </div>
                    <button type='submit' className='am-login__btn' disabled={loginLoading}>
                        {loginLoading ? 'SIGNING IN...' : 'SIGN IN'}
                    </button>
                </form>
            </div>
        );
    }

    if (!isAuthorized) {
        return (
            <div className='am-container'>
                <h1 className='am-title'>ADMIN</h1>
                <div className='am-denied'>
                    <p>Access denied. You do not have admin privileges.</p>
                    <button className='am-denied__btn' onClick={logout}>SIGN OUT</button>
                </div>
            </div>
        );
    }

    return (
        <div className='am-container'>
            <div className='am-header'>
                <h1 className='am-title'>MENU EDITOR</h1>
                <div className='am-header__actions'>
                    <a className='am-link' href='/menu' target='_blank' rel='noopener noreferrer'>View live menu ↗</a>
                    <button className='am-logout' onClick={logout}>SIGN OUT</button>
                </div>
            </div>

            {loading ? (
                <p className='am-empty'>Loading menu…</p>
            ) : (
                <>
                    <p className='am-hint'>
                        Edit anything below, then press <strong>Save Menu</strong>. Changes go live on
                        the website immediately. Leave a price blank to hide it. Empty items are removed on save.
                    </p>

                    <div className='am-section am-milk-panel'>
                        <div className='am-section__head'>
                            <span className='am-section__name am-milk-title'>
                                Milk Options (Latte, Cappuccino, Mocha)
                            </span>
                        </div>
                        <p className='am-milk-hint'>
                            Mark a milk unavailable when you run out — customers won&rsquo;t be able to pick it.
                        </p>
                        {milkOptions.map((m, idx) => (
                            <div className='am-item' key={m.name}>
                                <div className='am-item__fields'>
                                    <span className='am-milk-name'>{m.name} milk</span>
                                </div>
                                <div className='am-btn-group'>
                                    <button
                                        className={`am-icon-btn am-toggle${m.available !== false ? ' am-toggle--on' : ''}`}
                                        title={m.available !== false
                                            ? 'Available — click to mark unavailable'
                                            : 'Unavailable — click to make available'}
                                        onClick={() => toggleMilk(idx)}
                                    >
                                        {m.available !== false ? 'Available' : 'Unavailable'}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {sections.map((section, si) => (
                        <div className='am-section' key={si}>
                            <div className='am-section__head'>
                                <input
                                    className='am-section__name'
                                    value={section.category}
                                    placeholder='Section name (e.g. Espresso)'
                                    onChange={(e) => setSectionName(si, e.target.value)}
                                />
                                <div className='am-btn-group'>
                                    <button className='am-icon-btn' title='Move section up' onClick={() => moveSection(si, -1)} disabled={si === 0}>↑</button>
                                    <button className='am-icon-btn' title='Move section down' onClick={() => moveSection(si, 1)} disabled={si === sections.length - 1}>↓</button>
                                    <button className='am-icon-btn am-icon-btn--danger' title='Delete section' onClick={() => removeSection(si)}>Delete</button>
                                </div>
                            </div>

                            <input
                                className='am-section__note'
                                value={section.note || ''}
                                placeholder='Optional note shown under the section heading (e.g. All drinks made with double espresso · Iced 12oz · Hot 10oz)'
                                onChange={(e) => setSectionNote(si, e.target.value)}
                            />

                            {section.items.map((item, ii) => (
                                <div className={`am-item${item.hidden ? ' am-item--hidden' : ''}`} key={ii}>
                                    <div className='am-item__fields'>
                                        <input
                                            className='am-input am-input--name'
                                            value={item.name}
                                            placeholder='Item name'
                                            onChange={(e) => setItemField(si, ii, 'name', e.target.value)}
                                        />
                                        <input
                                            className='am-input am-input--price'
                                            value={item.price}
                                            placeholder='Price (e.g. $5.00)'
                                            onChange={(e) => setItemField(si, ii, 'price', e.target.value)}
                                        />
                                        <input
                                            className='am-input am-input--desc'
                                            value={item.desc}
                                            placeholder='Short description (optional)'
                                            onChange={(e) => setItemField(si, ii, 'desc', e.target.value)}
                                        />
                                    </div>
                                    <div className='am-btn-group'>
                                        <button
                                            className={`am-icon-btn am-toggle${item.hidden ? '' : ' am-toggle--on'}`}
                                            title={item.hidden ? 'Hidden from menu — click to show' : 'Showing on menu — click to hide'}
                                            onClick={() => setItemField(si, ii, 'hidden', !item.hidden)}
                                        >
                                            {item.hidden ? 'Hidden' : 'Shown'}
                                        </button>
                                        <button className='am-icon-btn' title='Move item up' onClick={() => moveItem(si, ii, -1)} disabled={ii === 0}>↑</button>
                                        <button className='am-icon-btn' title='Move item down' onClick={() => moveItem(si, ii, 1)} disabled={ii === section.items.length - 1}>↓</button>
                                        <button className='am-icon-btn am-icon-btn--danger' title='Delete item' onClick={() => removeItem(si, ii)}>✕</button>
                                    </div>
                                </div>
                            ))}

                            <button className='am-add-item' onClick={() => addItem(si)}>+ Add item</button>
                        </div>
                    ))}

                    <button className='am-add-section' onClick={addSection}>+ Add section</button>

                    <div className='am-save-bar'>
                        {status && <span className='am-status'>{status}</span>}
                        <button className='am-reset' onClick={resetToDefault}>Reset to default</button>
                        <button className='am-save' onClick={handleSave} disabled={saving}>
                            {saving ? 'SAVING…' : 'SAVE MENU'}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

export default AdminMenu;
